import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { getWaContactDisplayLabel } from '@common-reducers/WhatsAppSelectors';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { userMessagesSelectors } from '@common-reducers/DBServiceThunks';
import { namespace } from '@common/config/constants';
import { createSelector } from '@reduxjs/toolkit';
import { getContactSerializedId } from './contact-serialized-id';
import ContactAvatar from '@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { contactRowSubtitle } from './contact-row-subtitle';
import { buildChannelNameIndex, resolveContactLabel } from './channel-name-resolver';

// #164: newsletter/channel names WhatsApp no longer surfaces in the live store
// are recovered from the user's own scheduled messages (contactList[].name).
// Memoised — rebuilt only when the scheduled-message set changes.
export const selectChannelNameIndex = createSelector(
    // DB-service selectors take the ROOT state (they apply their own
    // getReducerState internally) — passing the slice makes selectAll read
    // `undefined.ids` and crash the picker. Match whatsapp-message.tsx's usage.
    [(state: any) => userMessagesSelectors.selectAll(state)],
    (messages) => buildChannelNameIndex(messages),
);

// Re-exported from ./contact-serialized-id (extracted for unit-testing; BE#272).
export { getContactSerializedId };

export interface UnifiedContactRowProps {
    contact: any;
    index?: number;
    showCheckbox?: boolean;
    // singleSelect renders a radio-style indicator instead of a checkbox.
    // Used by edit-cell flows where picking a different contact replaces the
    // current value (rather than toggling membership in a multi-select).
    singleSelect?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    onClick?: (contact: any) => void;
    /** A quiet second line under the name (e.g. "Not in your contacts"). */
    subtitle?: string;
}

export default memo(function UnifiedContactRow({
    contact,
    index,
    showCheckbox = false,
    singleSelect = false,
    isSelected = false,
    isDisabled = false,
    onClick,
    subtitle,
}: UnifiedContactRowProps) {
    const contactId = getContactSerializedId(contact);
    const { t } = useTranslation(namespace);
    const channelNameIndex = useAppSelector(selectChannelNameIndex);
    // #164: for a channel, resolve the real name (contact fields → harvested
    // from scheduled messages → neutral "Channel"); never render the JID as a
    // fake "+120363…" phone number. Non-channels keep their existing label.
    const displayLabel = resolveContactLabel(
        contact,
        channelNameIndex,
        getWaContactDisplayLabel(contact),
        t('contacts.channelFallback', 'Channel'),
    );

    // #294: archived chats are searchable (the cache keeps them); mark them on the quiet second line.
    const rowSubtitle = contactRowSubtitle(contact, subtitle, t('contacts.archived', 'Archived'));

    const handleClick = useCallback(() => {
        if (!isDisabled) onClick?.(contact);
    }, [isDisabled, onClick, contact]);

    return (
        <div
            key={index}
            onClick={handleClick}
            className={cn(
                "flex flex-row items-center h-[72px] w-full box-border px-4 cursor-pointer transition-all duration-200 relative",
                !isDisabled && "hover:bg-[var(--tasks-row-hover-bg)]",
                isDisabled && "cursor-not-allowed opacity-50",
                isSelected && "bg-blue-50/50 dark:bg-blue-900/10",
            )}
        >
            {isDisabled && (
                <div className="absolute inset-0 bg-[var(--date-badge-bg)] z-10 pointer-events-none" />
            )}

            {showCheckbox && (
                <div
                    /* `me-3`, not `mr-3`: a physical margin-right stays on the
                       right in Hebrew/RTL, where this column sits to the RIGHT
                       of the name — so the gap opened away from the name and the
                       two ran together. */
                    className="flex items-center justify-center flex-shrink-0 w-10 me-3"
                    onClick={e => e.stopPropagation()}
                    onPointerDown={e => e.stopPropagation()}
                >
                    {singleSelect ? (
                        // Plain radio markup (not a RadioGroupItem) — we don't have a
                        // RadioGroup wrapper around the virtualized list. The row's
                        // own onClick still drives selection state via handleClick.
                        <button
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            disabled={isDisabled}
                            onClick={() => handleClick()}
                            className={cn(
                                "h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors",
                                isSelected
                                    ? "border-blue-600 dark:border-blue-500"
                                    : "border-slate-700 dark:border-slate-400"
                            )}
                        >
                            {isSelected && (
                                <span className="h-2.5 w-2.5 rounded-full bg-blue-600 dark:bg-blue-500" />
                            )}
                        </button>
                    ) : (
                        <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleClick()}
                            disabled={isDisabled}
                            className={cn(
                                "h-5 w-5 border-slate-700 dark:border-slate-400",
                                isSelected && "border-blue-600 dark:border-blue-500 data-[state=checked]:bg-blue-600 dark:data-[state=checked]:bg-blue-500 data-[state=checked]:text-white"
                            )}
                        />
                    )}
                </div>
            )}

            {/* `me-3`, not `mr-3` — the avatar/name gap, for the same RTL reason. */}
            <div className="flex items-center justify-center flex-shrink-0 me-3">
                <ContactAvatar
                    contactId={contactId ?? ''}
                    profilePicURL={contact?.imgUrl}
                    size={44}
                    textSize={16}
                    // The list's useWaContactsLazyLoad hook batch-fetches pics for
                    // the visible range, so the row avatar must not self-fetch
                    // (that would preempt the batch with one-by-one requests).
                    dontFetchProfilePicFromDB
                />
            </div>

            <div className={cn(
                "flex items-center flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap h-full",
                "text-[var(--tasks-text-secondary)] font-medium",
                isSelected && "font-semibold text-[var(--color-primary)]",
                rowSubtitle && "flex-col items-start justify-center",
            )}>
                {rowSubtitle ? (
                    <>
                        <span className="max-w-full truncate">{displayLabel}</span>
                        <span className="max-w-full truncate text-xs font-normal text-[var(--color-text-muted)]">{rowSubtitle}</span>
                    </>
                ) : displayLabel}
            </div>
        </div>
    );
});
